"use client";
import styled from 'styled-components';
import BackButton from '../utils/back_button';
import {blog17} from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import Navigation from "../../navigation/header"
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"
import Footer from "../../Footer/footer"

export default function Blog17() {
 const navigate = useNavigate();
          const handleBackClick = () => {
             window.scrollTo(0, 0);
             navigate("/blog")
          };

  return (
    <>
      <Fullpage>
        <PageBackground>

          <BlogContainer>
          <MainContent>
              <HeaderSection>
                <BackButtonWrapper>
                  <BackButton onClick={handleBackClick} />
                </BackButtonWrapper>
                <BlogHeader
                  title="Why First-Year Retention Is the Canary in the Coal Mine for College Enrollment"
                  date="October 07 2025"
                  author="POSTED BY CAMPUS INSIGHTS"
                />
              </HeaderSection>

              <HeroImage
                src={blog17}
                alt="College enrollment feature illustration"
              />

            <BlogContent />
          </MainContent>
          </BlogContainer>

        <OtherBlogs currentBlogId={17} />
        </PageBackground>
      </Fullpage>
      
      <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  return (
    <HeaderContainer>
      <Title>{title}</Title>
      <MetadataContainer>
        <MetadataWrapper>
          <MetadataGroup>
            <DateText>{date}</DateText>
            <AuthorText>{author}</AuthorText>
          </MetadataGroup>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <IntroSection>
        <IntroParagraph>
          Across the country, colleges are contending with shrinking enrollment, but one measure matters as much as who arrives: who stays. First-year retention—the percentage of students who return for their second year—is both an early warning signal and a powerful lever for institutional health.
        </IntroParagraph>
        <IntroParagraph>
          According to the <a href='https://nscresearchcenter.org/persistence-retention/' target='_blank'>National Student Clearinghouse</a>, the second-year retention rate for the Fall 2023 cohort was 69.5%, while second-year persistence—which includes students who transfer—was 77.6%.
        </IntroParagraph>
        <IntroParagraph>
          These numbers, while slightly improved from the previous year, still reflect a troubling reality: nearly one in four students who begin college do not return to any institution the following fall.
        </IntroParagraph>
      </IntroSection>

      <ContentSection title="The Ripple Effect of Attrition">
        <ContentParagraph>
          When students leave, the consequences extend far beyond enrollment reports. For enrollment managers, hard-won recruitment efforts unravel in just one semester. Faculty spot the warning signs—missed assignments, slipping engagement—but lack the tools or time to intervene. Advisors juggle mounting caseloads alongside financial and mental health crises. Administrators face the dual challenge of closing budget gaps while investing in strategies that improve outcomes.
        </ContentParagraph>
        <ContentParagraph>
          Each departure is more than lost tuition revenue—it’s a missed chance to fulfill the institution’s mission and a setback for the student’s future.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="Beyond Headcounts: Why Credit Completion Matters">
        <ContentParagraph>
          One underutilized but powerful metric is the credit completion ratio (CCR)—the percentage of credits attempted that are successfully completed.
        </ContentParagraph>
        <ContentParagraph>
          A low CCR signals that students are falling behind academically, which often precedes withdrawal. Tracking CCR alongside first-year persistence gives colleges a fuller picture of student momentum.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="The Part-Time Gap">
        <ContentParagraph>
          Part-time students are especially vulnerable. The Clearinghouse reports that first spring persistence for part-time students was just 67.4%, compared to 92.1% for full-time peers. This gap underscores the need for tailored interventions that address time, financial strain, and support access.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="From Reactive to Proactive">
        <ContentParagraph>
          Another benefit I’ve come to appreciate is the LMS’s ability to recommend content—not just to students, but to me. Based on course objectives and student behavior, the system suggests articles, videos, simulations, and even open educational resources that align with my curriculum. It’s like having a librarian and instructional designer rolled into one.
        </ContentParagraph>
        <ContentParagraph>
          This has been especially helpful in interdisciplinary courses, where I’m not always the subject matter expert in every topic. The AI helps me curate high-quality materials that enrich the learning experience without hours of searching.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="How It Works">
        <ProcessDescription>
          Improving retention requires more than crisis response. It demands proactive, data-informed strategies that put the student experience at the center. That means:
        </ProcessDescription>
        <FeatureList>
          <FeatureItem>Early alerts for at-risk students</FeatureItem>
          <FeatureItem>Personalized outreach at critical moments</FeatureItem>
          <FeatureItem>Wraparound support services that address academic, financial, and emotional needs</FeatureItem>
          <FeatureItem>Listening—really listening—to what students say about their barriers and aspirations</FeatureItem>
        </FeatureList>
      </ContentSection>

      <ContentSection title="Retention as a Sustainability Strategy">
        <ContentParagraph>
          For institutions facing enrollment declines, retention isn’t just a student success issue—it’s a sustainability issue. Every student who leaves represents not only lost tuition revenue, but also a missed opportunity to fulfill the institution’s mission. And for students, departure often means debt without a degree, and diminished confidence in higher education.
        </ContentParagraph>
        <ContentParagraph>
          The question isn’t just “How do we get students in the door?” It’s “How do we help them stay, succeed, and graduate?” That’s where transformation begins—not in marketing campaigns, but in meaningful, measurable support.
        </ContentParagraph>
      </ContentSection>
    </ContentContainer>
  );
};

const ContentSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <ContentWrapper>{children}</ContentWrapper>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const SectionTitle = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 28px;
  }

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
  a {
    color: #5e2bff; /* Purple accent */
    text-decoration: underline;
    font-weight: 600;
  }

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ContentParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const Title = styled.h1`
  width: 1021px;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    width: 100%;
    font-size: 36px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;


const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;

  @media (max-width: 991px) {
    width: 100%;
    max-width: 768px;
    padding: 40px 32px;
    gap: 48px;
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    padding: 24px 16px;
    gap: 32px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const HeroImage = styled.img`
  width: 1008px;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    width: 100%;
    height: 300px;
  }

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  list-style-type: none;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '•';
    color: #64636a;
    position: absolute;
    left: 0;
    top: 0;
    font-weight: 700;
    line-height: 160%;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
    &::before {
      line-height: 150%;
    }
  }
`;

const ProcessDescription = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;